import { Client } from '@/types/Client';
import { EmploymentType, Stepper, WorkType } from '@/types/enums';
import { Job } from '@/types/Job';
import { create } from 'zustand';

interface CreateJobStore {
  step: Stepper;
  currentPage: Stepper;
  selectedClient: Client | null;
  jdFile: File | null;
  jobTitle: string;
  category: string;
  currency: Currency | null;
  salaryFrom: number | null;
  salaryTo: number | null;
  employmentType: EmploymentType | null;
  workType: WorkType | null;
  location: string;
  jobDescription: string;
  contactName: string;
  contactRole: string;
  contactEmail: string;
  contactPhone: string;
  requirements: string[];
  niceToHave: string[];
  position_tags: string[];
  newJob: Job | null;

  setStep: (step: Stepper) => void;
  setCurrentPage: (step: Stepper) => void;
  setSelectedClient: (client: Client | null) => void;
  isStepEnabled: (checkStep: Stepper) => boolean;
  setJdFile: (file: File | null) => void;
  setJobTitle: (title: string) => void;
  setCategory: (category: string) => void;
  setCurrency: (currency: Currency) => void;
  setSalaryFrom: (salary: number | null) => void;
  setSalaryTo: (salary: number | null) => void;
  setEmploymentType: (type: EmploymentType) => void;
  setWorkType: (type: WorkType) => void;
  setLocation: (location: string) => void;
  setJobDescription: (description: string) => void;
  setContactName: (name: string) => void;
  setContactRole: (role: string) => void;
  setContactEmail: (email: string) => void;
  setContactPhone: (phone: string) => void;
  setRequirements: (requirements: string[]) => void;
  setNiceToHave: (niceToHave: string[]) => void;
  setPositionTags: (position_tags: string[]) => void;
  updateRequirement: (index: number, text: string) => void;
  addRequirement: () => void;
  removeRequirement: (index: number) => void;
  updateNiceToHave: (index: number, text: string) => void;
  addNiceToHave: () => void;
  removeNiceToHave: (index: number) => void;
  setNewJob: (job: Job) => void;
  reset: () => void;
}

export const useCreateJob = create<CreateJobStore>((set, get) => ({
  step: Stepper.Client,
  currentPage: Stepper.Client,
  selectedClient: null,
  jdFile: null,
  jobTitle: '',
  category: '',
  currency: null,
  salaryFrom: null,
  salaryTo: null,
  employmentType: null,
  workType: null,
  location: '',
  jobDescription: '',
  contactName: '',
  contactRole: '',
  contactEmail: '',
  contactPhone: '',
  requirements: [],
  niceToHave: [],
  position_tags: [],
  newJob: null,

  setStep: (step) => set({ step }),
  setCurrentPage: (step) => set({ currentPage: step }),
  setSelectedClient: (client) => set({ selectedClient: client }),
  isStepEnabled: (checkStep) => {
    const currentStep = get().step;
    return (
      currentStep === Stepper.Review ||
      (currentStep === Stepper.AiAnalysis && checkStep !== Stepper.Review) ||
      (currentStep === Stepper.JobDetails &&
        checkStep !== Stepper.AiAnalysis &&
        checkStep !== Stepper.Review) ||
      (currentStep === Stepper.Client && checkStep === Stepper.Client)
    );
  },
  setJdFile: (file) => set({ jdFile: file }),
  setJobTitle: (title) => set({ jobTitle: title }),
  setCategory: (category) => set({ category }),
  setCurrency: (currency) => set({ currency }),
  setSalaryFrom: (salary) => set({ salaryFrom: salary }),
  setSalaryTo: (salary) => set({ salaryTo: salary }),
  setEmploymentType: (type) => set({ employmentType: type }),
  setWorkType: (type) => set({ workType: type }),
  setLocation: (location) => set({ location }),
  setJobDescription: (description) => set({ jobDescription: description }),
  setContactName: (name) => set({ contactName: name }),
  setContactRole: (role) => set({ contactRole: role }),
  setContactEmail: (email) => set({ contactEmail: email }),
  setContactPhone: (phone) => set({ contactPhone: phone }),
  setRequirements: (requirements) => set({ requirements }),
  setNiceToHave: (niceToHave) => set({ niceToHave }),
  setPositionTags: (position_tags) => set({ position_tags }),
  updateRequirement: (index, text) => {
    const requirements = [...get().requirements];
    requirements[index] = text;
    set({ requirements });
  },
  addRequirement: () => {
    const requirements = [...get().requirements, ''];
    set({ requirements });
  },
  removeRequirement: (index) => {
    const requirements = get().requirements.filter((_, i) => i !== index);
    set({ requirements });
  },
  updateNiceToHave: (index, text) => {
    const niceToHave = [...get().niceToHave];
    niceToHave[index] = text;
    set({ niceToHave });
  },
  addNiceToHave: () => {
    const niceToHave = [...get().niceToHave, ''];
    set({ niceToHave });
  },
  removeNiceToHave: (index) => {
    const niceToHave = get().niceToHave.filter((_, i) => i !== index);
    set({ niceToHave });
  },
  setNewJob: (job) => set({ newJob: job }),
  reset: () =>
    set({
      selectedClient: null,
      step: Stepper.Client,
      currentPage: Stepper.Client,
      jdFile: null,
      jobTitle: '',
      category: '',
      salaryFrom: null,
      salaryTo: null,
      employmentType: null,
      workType: null,
      location: '',
      jobDescription: '',
      contactName: '',
      contactRole: '',
      contactEmail: '',
      contactPhone: '',
      requirements: [],
      niceToHave: [],
      newJob: null
    })
}));
